// ─────────────────────────────────────────────
//  VoiceControl — 🎤 voice commands for the OR board.
//
//  Two modes, one surface:
//   • Operational (AIC / doctor / board manager / admin): speak a request —
//     "assign a CRNA to OR 5", "we need help in OR 2 and 3", "rotate breaks",
//     "what's our supervision ratio" — the intent parser + planner propose
//     concrete actions; NOTHING executes without an explicit Confirm tap.
//   • Admin (real admin only): anything the parser doesn't recognize becomes a
//     structured bug/issue report (transcript + live app-state snapshot +
//     recent console errors) written to voice_reports for the dev loop.
//
//  Speech layer: Web Speech API (push-to-talk). A typed-command field is the
//  always-available fallback (also what automated tests drive). Every command
//  and decision is audit-logged to voice_logs (append-only).
//
//  Intelligence layer: deterministic parser (domain/voiceIntent) is the
//  always-works baseline. When the ANTHROPIC_API_KEY secret is configured and
//  functions/voiceIntent.js is exported, richer parsing can be layered in —
//  the confirm-before-execute contract stays identical.
// ─────────────────────────────────────────────
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { parseVoiceIntent } from "../domain/voiceIntent";
import { planVoiceIntent } from "../domain/voicePlan";
import { assignCover } from "../domain/breaks";
import {
  updateAssignmentRow, createAssignment, setRoomAssignment, setRoomBreak,
  logVoiceCommand, createVoiceReport,
} from "../firebase/firestore";
import tokens from "../theme/tokens";

const { color, radius, shadow, font, space } = tokens;

// Recent console errors (ring buffer) — attached to admin voice reports so a
// spoken "X is broken" carries the evidence with it.
const RECENT_ERRORS = [];
if (typeof window !== "undefined" && !window.__atcVoiceErrHook) {
  window.__atcVoiceErrHook = true;
  window.addEventListener("error", (e) => {
    RECENT_ERRORS.push(`${new Date().toISOString()} ${e.message} @ ${e.filename}:${e.lineno}`);
    if (RECENT_ERRORS.length > 10) RECENT_ERRORS.shift();
  });
  window.addEventListener("unhandledrejection", (e) => {
    RECENT_ERRORS.push(`${new Date().toISOString()} unhandledrejection: ${e.reason?.message || e.reason}`);
    if (RECENT_ERRORS.length > 10) RECENT_ERRORS.shift();
  });
}

const SR = typeof window !== "undefined"
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

export default function VoiceControl({ rooms = [], assignments = [], bench = [], breakGivers = [], facilityId, date }) {
  const { user, realIsAdmin, canManageAssignments } = useAuth();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [proposals, setProposals] = useState(null); // null = nothing reviewed yet
  const [busy, setBusy] = useState(false);
  const recRef = useRef(null);

  useEffect(() => () => { try { recRef.current?.stop(); } catch { /* noop */ } }, []);

  // For now the mic is ADMIN-ONLY (user directive 2026-07-07): hidden for
  // AICs/doctors/BMs until the team is ready to roll it out wider.
  if (!realIsAdmin) return null;

  const review = (text) => {
    const intent = parseVoiceIntent(text, { rooms, isAdmin: realIsAdmin });
    const plan = planVoiceIntent(intent, { rooms, assignments, bench, breakGivers });
    setTranscript(text);
    setProposals({ intent, plan });
  };

  const startListening = () => {
    if (!SR) { toast.info("Voice not supported in this browser — type the command instead."); return; }
    try {
      const rec = new SR();
      recRef.current = rec;
      rec.lang = "en-US";
      rec.interimResults = true;
      rec.continuous = false;
      let finalText = "";
      rec.onresult = (e) => {
        let interim = "";
        for (const r of e.results) {
          if (r.isFinal) finalText += r[0].transcript;
          else interim += r[0].transcript;
        }
        setTranscript(finalText || interim);
      };
      rec.onend = () => {
        setListening(false);
        if (finalText.trim()) review(finalText.trim());
      };
      rec.onerror = (e) => {
        setListening(false);
        if (e.error === "not-allowed") toast.error("Microphone blocked — allow it in site settings, or type the command.");
      };
      setTranscript("");
      setProposals(null);
      setListening(true);
      rec.start();
    } catch (err) {
      console.error("Speech start failed:", err);
      setListening(false);
      toast.error("Couldn't start the microphone — type the command instead.");
    }
  };
  const stopListening = () => { try { recRef.current?.stop(); } catch { /* noop */ } };

  const audit = async (decision, extra = {}) => {
    try {
      await logVoiceCommand({
        uid: user?.uid, name: user?.name || "", role: user?.role || "",
        facilityId: facilityId || null, date: date || null,
        transcript, intent: proposals?.intent?.type || "none",
        decision, ...extra,
      });
    } catch (e) { console.error("Voice audit failed:", e); }
  };

  const execute = async (p) => {
    setBusy(true);
    try {
      if (p.kind === "assign") {
        if (p.assignmentId) {
          await updateAssignmentRow(p.assignmentId, {
            ahpId: p.providerId, ahpName: p.providerName, ahpShiftCode: null, ahpDepartTime: "",
          });
        } else {
          await createAssignment({
            facilityId, date,
            orRoom: String(p.roomName).replace(/^OR\s+/i, ""),
            ahpId: p.providerId, ahpName: p.providerName,
          });
        }
        await setRoomAssignment(p.roomId, {
          providerId: p.providerId, providerName: p.providerName, providerRole: "CRNA",
        });
        toast.success(`${p.providerName} → ${p.roomName}.`);
      } else if (p.kind === "break") {
        const room = rooms.find((r) => r.id === p.roomId);
        // A voice command NAMES a cover; it cannot put them in the room.
        // `onBreak` stays false until that person's own arrival tap.
        const breaks = assignCover(room?.breaks || {}, p.channel);
        await setRoomBreak(p.roomId, {
          breaks, channel: p.channel,
          relieverId: p.giverId, relieverName: p.giverName,
          onBreak: false,
        });
        toast.success(`${p.giverName} on the way to cover ${p.channel} in ${p.roomName}.`);
      } else if (p.kind === "report") {
        await createVoiceReport({
          uid: user?.uid, name: user?.name || "",
          transcript: p.text,
          snapshot: {
            facilityId: facilityId || null, date: date || null,
            route: typeof window !== "undefined" ? window.location.pathname : "",
            roomsOnBoard: rooms.length,
            staffedRows: assignments.filter((a) => a.ahpId || a.attendingId).length,
            recentErrors: [...RECENT_ERRORS],
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
          },
        });
        toast.success("Filed for the dev loop — with a live app-state snapshot attached.");
      }
      await audit("approved", { action: p.kind, label: p.label || "" });
      // Remove the executed proposal; close when none are left.
      setProposals((prev) => {
        const rest = prev.plan.filter((x) => x !== p);
        return rest.some((x) => x.kind !== "answer") ? { ...prev, plan: rest } : null;
      });
    } catch (err) {
      console.error("Voice action failed:", err);
      toast.error("That action failed — nothing else was changed.");
      await audit("failed", { action: p.kind, error: String(err?.message || err) });
    } finally {
      setBusy(false);
    }
  };

  const dismissAll = async () => {
    if (proposals) await audit("cancelled");
    setProposals(null);
    setTranscript("");
  };

  const answers = proposals?.plan.filter((p) => p.kind === "answer") || [];
  const actions = proposals?.plan.filter((p) => p.kind !== "answer") || [];

  return (
    <>
      <button
        type="button"
        style={{ ...st.micBtn, ...(open ? st.micBtnOpen : {}) }}
        onClick={() => setOpen((o) => !o)}
        aria-label="Voice commands"
        title="Voice commands"
      >
        🎤
      </button>

      {open && createPortal(
        <div style={st.panel} role="dialog" aria-label="Voice commands">
          <div style={st.head}>
            <span style={{ fontWeight: 700 }}>🎤 Voice command</span>
            <button style={st.x} onClick={() => { setOpen(false); dismissAll(); }} aria-label="Close voice panel">✕</button>
          </div>

          <div style={st.row}>
            <button
              style={{ ...st.talkBtn, ...(listening ? st.talkBtnLive : {}) }}
              onClick={listening ? stopListening : startListening}
              aria-pressed={listening}
            >
              {listening ? "◼ Stop" : "● Speak"}
            </button>
            <input
              style={st.input}
              placeholder='…or type: "assign a CRNA to OR 5"'
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && transcript.trim()) review(transcript.trim()); }}
              aria-label="Type a voice command"
            />
            <button style={st.goBtn} disabled={!transcript.trim()} onClick={() => review(transcript.trim())}>Go</button>
          </div>
          {listening && <div style={st.hint}>Listening… speak the request, then Stop.</div>}

          {answers.map((a, i) => (
            <div key={`ans-${i}`} style={st.answer} aria-live="polite">{a.text}</div>
          ))}

          {actions.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
              <div style={st.confirmNote}>Nothing happens until you confirm:</div>
              {actions.map((p, i) => (
                <div key={i} style={st.proposal}>
                  <span style={{ flex: 1, fontSize: 13 }}>{p.label}</span>
                  <button style={st.confirmBtn} disabled={busy} onClick={() => execute(p)}>✓ Confirm</button>
                </div>
              ))}
              <button style={st.cancelBtn} disabled={busy} onClick={dismissAll}>Cancel all</button>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

const st = {
  micBtn: { background: color.surface.raised, border: `1px solid ${color.border.default}`, borderRadius: radius.full, width: 36, height: 36, fontSize: 16, cursor: "pointer", flexShrink: 0 },
  micBtnOpen: { border: `2px solid ${color.accent.primary}` },
  panel: { position: "fixed", right: 16, bottom: 76, zIndex: 260, width: 400, maxWidth: "94vw", background: color.surface.card, border: `1px solid ${color.border.default}`, borderRadius: radius.lg, boxShadow: shadow.lg, padding: 14, fontFamily: font.family },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, fontSize: 14, color: color.text.primary },
  x: { background: "transparent", border: "none", fontSize: 14, cursor: "pointer", color: color.text.muted },
  row: { display: "flex", gap: 8, alignItems: "center" },
  talkBtn: { border: `1px solid ${color.border.default}`, background: color.surface.raised, borderRadius: radius.sm, padding: "8px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" },
  talkBtnLive: { background: "#fee2e2", borderColor: "#dc2626", color: "#b91c1c" },
  input: { flex: 1, minWidth: 0, border: `1px solid ${color.border.default}`, borderRadius: radius.sm, padding: "8px 10px", fontSize: 13, fontFamily: "inherit" },
  goBtn: { border: `1px solid ${color.accent.primary}`, background: color.accent.primary, color: "#fff", borderRadius: radius.sm, padding: "8px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  hint: { fontSize: 12, color: color.text.muted, marginTop: 6 },
  answer: { marginTop: 10, fontSize: 13, lineHeight: 1.5, background: color.surface.raised, border: `1px solid ${color.border.default}`, borderRadius: radius.sm, padding: "10px 12px", color: color.text.primary },
  confirmNote: { fontSize: 11, color: color.text.muted, textTransform: "uppercase", letterSpacing: ".6px" },
  proposal: { display: "flex", alignItems: "center", gap: 10, background: color.surface.raised, border: `1px solid ${color.border.default}`, borderRadius: radius.sm, padding: "10px 12px" },
  confirmBtn: { border: "1px solid #0a7d5e", background: "#0a7d5e", color: "#fff", borderRadius: radius.sm, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" },
  cancelBtn: { border: `1px solid ${color.border.default}`, background: "transparent", color: color.text.secondary, borderRadius: radius.sm, padding: "8px 12px", fontSize: 12, cursor: "pointer" },
};
